import React, { useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'
import Footer from '@app/components/Footer/Footer'
import Navbar from '@app/components/Navbar/Navbar'
import TextField from '@app/components/TextField/TextField'
import Button from '@app/components/Button/Button'
import { useNavigate, useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useFormik } from 'formik'
import Tippy from '@tippyjs/react'
import Checkbox from '@app/components/Checkbox/Checkbox'
import { usePostOrderMutation } from '@app/store/api/customer'
import { useAppSelector, useAppDispatch } from '@app/store/hooks'
import { QuizStep, updateQuiz } from '@app/store/slices/quiz'
import { parse, format } from 'date-fns'
import { saveOrder, setCurrentOrder } from '@app/store/slices/order'
import type { ProgramCode } from '@app/programs/programs.types'
import ReactMarkdown from 'react-markdown'
import useCampaignHref from '@app/hooks/useCampaignHref'
import urls from '@app/routes/urls'
import CustomLink from '@app/components/CustomLink'
import remarkGfm from 'remark-gfm'
import S from './styles'
import QuizHeader from './components/QuizHeader/QuizHeader'
import ConfirmDialog from './components/ConfirmDialog'
import useQuizRedirect from './useQuizRedirect'
import RedirectPartialView from './RedirectPartialView'
import { getTradedSkuForDEAndAT } from './utils'

const PaymentDetails = () => {
  const quiz = useAppSelector(state => state.quiz)
  const { program } = useParams<{ program: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { messages, formatMessage } = useIntl()
  const [post, res] = usePostOrderMutation()
  const [showConfirm, setShowConfirm] = useState(false)

  const { href: orderHref } = useCampaignHref({ requestPath: `order-complete/${res?.data?.order_id}` })
  const { href: termsUrl } = useCampaignHref({ requestPath: urls.terms })
  const { href: privacyUrl } = useCampaignHref({ requestPath: urls.privacy })
  const { isRedirecting } = useQuizRedirect({ requestedStep: QuizStep.PAYMENT })

  const parsedDate = useMemo(() => {
    if (!quiz.purchaseDate) return ''
    const actualDate = parse(quiz.purchaseDate, 'dd/MM/yyyy', new Date())
    return format(actualDate, 'yyyy-MM-dd')
  }, [quiz.purchaseDate])

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      accountHolder: '',
      iban: '',
      acceptsTerms: false,
    },
    validationSchema: yup.object({
      accountHolder: yup
        .string()
        .isValidName(messages['static.form.error_account_invalid'] as string)
        .required(messages['quiz_payment_page.content.questions.account_holder.validation_message'] as string),
      iban: yup
        .string()
        .iban(messages['static.form.error_iban_invalid'] as string)
        .required(messages['quiz_payment_page.content.questions.iban.validation_message'] as string),
      acceptsTerms: yup.boolean().oneOf([true], messages['static.form.error_agree_terms'] as string),
    }),
    onSubmit: () => {
      setShowConfirm(true)
    },
  })

  /**
   * Trims any leading or trailing white space from the value in memory
   */
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.trim()
    formik.handleChange(e)
  }

  const handleSubmit = () => {
    post({
      user_id: quiz.customerId,
      // The program URL param is an essential part of the pathname and will always be defined because of
      // the redirection logic in CustomerApp.tsx (redirects to /404 if the program is not found or not valid)
      promo_code: program ? (program.toUpperCase() as ProgramCode) : '',
      order_item: {
        product_model: quiz.purchaseModel,
        serial_number: quiz.purchaseSerial,
        purchased_date: parsedDate,
        purchased_store: quiz.purchaseVendor,
        manufacturer: quiz.tradeInManufacturer,
        working_condition: quiz.tradeInWorking,
        traded_serial: quiz.tradeInSerial,
        traded_sku: getTradedSkuForDEAndAT(quiz.tradeInManufacturer, quiz.purchaseSerial),
      },
      payment_details: {
        name: formik.values.accountHolder,
        iban: formik.values.iban,
      },
      shipping_details: {
        name: quiz.customerName,
        address: quiz.shippingAddressLine1,
        postal_code: quiz.shippingAddressPostcode,
        city: quiz.shippingAddressCity,
        country: quiz.shippingAddressCountryCode,
      },
    })
  }

  /**
   * Update the store with the order details if the request was successful
   */
  useEffect(() => {
    const currentRes = res
    if (currentRes.isSuccess) {
      // Obscure the IBAN
      const iban = '*'.repeat(formik.values.iban.length - 4) + formik.values.iban.substr(-4)

      dispatch(setCurrentOrder(currentRes.data.order_id))

      dispatch(
        saveOrder({
          customerName: quiz.customerName.split(' ')[0],
          tradeInManufacturer: quiz.tradeInManufacturer ?? '',
          paymentAccountHolder: formik.values.accountHolder,
          paymentIban: iban
            .split('')
            .map((o, i) => (i % 4 === 0 ? ` ${o}` : o))
            .join(''),
        })
      )

      dispatch(
        updateQuiz({
          paymentAccountHolder: formik.values.accountHolder,
          paymentIban: formik.values.iban,
          // These are cleared when the order confirmation page is loaded
          step: QuizStep.COMPLETE,
          prevStep: QuizStep.PAYMENT,
        })
      )
    }
  }, [dispatch, res, formik.values, quiz.customerEmail, quiz.customerName, quiz.tradeInManufacturer])

  /**
   * Only trigger navigation after the step is updated in the store
   */
  useEffect(() => {
    if (quiz.step === QuizStep.COMPLETE && res?.data?.order_id) {
      navigate(orderHref)
    }
  }, [navigate, quiz.step, res, orderHref])

  if (isRedirecting) {
    return <RedirectPartialView />
  }

  return (
    <>
      <Navbar />
      <S.QuizMain>
        <QuizHeader
          title={<FormattedMessage id="quiz_payment_page.content.base.title" />}
          intro={<FormattedMessage id="quiz_payment_page.content.base.intro" />}
          nextStep={<FormattedMessage id="quiz_payment_page.content.base.button_text" />}
          step={4}
          progress={78}
        />
        <S.Form onSubmit={formik.handleSubmit}>
          <TextField
            name="accountHolder"
            label={messages['quiz_payment_page.content.questions.account_holder.label'] as string}
            placeholder={messages['quiz_payment_page.content.questions.account_holder.placeholder'] as string}
            onChange={handleNameChange}
            onBlur={formik.handleBlur}
            error={
              Boolean(formik.errors.accountHolder) && Boolean(formik.touched.accountHolder)
                ? formik.errors.accountHolder
                : undefined
            }
          />
          <TextField
            name="iban"
            label={messages['quiz_payment_page.content.questions.iban.label'] as string}
            placeholder={messages['quiz_payment_page.content.questions.iban.placeholder'] as string}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.errors.iban) && Boolean(formik.touched.iban) ? formik.errors.iban : undefined}
            mask={{
              blocks: Array(8).fill(4),
              delimiter: ' ',
            }}
            returnRawValue
          />
          <Checkbox
            id="termsCheckbox"
            name="acceptsTerms"
            value="test"
            checked={formik.values.acceptsTerms}
            onChange={formik.handleChange}>
            <ReactMarkdown components={{ a: CustomLink }} remarkPlugins={[remarkGfm]}>
              {
                formatMessage(
                  { id: 'quiz_payment_page.content.questions.agree_to_terms.checkbox_label' },
                  { terms_url: termsUrl, privacy_url: privacyUrl }
                ) as string
              }
            </ReactMarkdown>
          </Checkbox>
          <S.ActionBar>
            <Tippy
              content={<FormattedMessage id="static.form.hint_submit" />}
              placement="bottom"
              disabled={formik.isValid}>
              <div>
                <Button type="submit" disabled={!formik.isValid}>
                  <FormattedMessage id="quiz_payment_page.content.base.button_text" />
                </Button>
              </div>
            </Tippy>
          </S.ActionBar>
        </S.Form>
      </S.QuizMain>
      <ConfirmDialog
        show={showConfirm}
        accountHolder={formik.values.accountHolder}
        iban={formik.values.iban}
        isSubmitting={res.isLoading}
        onExit={() => setShowConfirm(false)}
        onSubmit={handleSubmit}
      />
      <Footer />
    </>
  )
}

export default PaymentDetails
